import { useState } from 'react';

// Types pour les états des modals
interface TimerModalState {
  isOpen: boolean;
  timer: { name: string; duration: number } | null;
}

interface ConductRulesModalState {
  isOpen: boolean;
}

interface PendingGameStartState {
  isPending: boolean;
}

interface GameEndModalState {
  isOpen: boolean;
  winner: string | null;
}

interface MatchStartModalState {
  isOpen: boolean;
}

interface WarningModalState {
  isOpen: boolean;
}

interface WarningStepState {
  step: number;
  selectedPlayer: 'A' | 'B' | null;
  selectedPlayerName: string;
}

interface ConductJustificationModalState {
  isOpen: boolean;
  player: 'A' | 'B' | null;
  action: string | null;
  playerName: string;
}

// État global des modals
interface ModalStates {
  timerModal: TimerModalState;
  gameEndModal: GameEndModalState;
  matchStartModal: MatchStartModalState;
  warningModal: WarningModalState;
  warningStep: WarningStepState;
  conductJustificationModal: ConductJustificationModalState;
  conductRulesModal: ConductRulesModalState;
  pendingGameStart: PendingGameStartState;
}

// Actions pour contrôler les modals
interface ModalActions {
  // Timer Modal
  openTimerSelection: () => void;
  openTimerWithTimer: (timer: { name: string; duration: number }) => void;
  closeTimer: () => void;
  
  // Game End Modal
  openGameEnd: (winner: string) => void;
  closeGameEnd: () => void;
  
  // Match Start Modal
  openMatchStart: () => void;
  closeMatchStart: () => void;
  
  // Warning Modal
  openWarning: () => void;
  closeWarning: () => void;
  setWarningStep: (step: number, player?: 'A' | 'B', playerName?: string) => void;
  
  // Conduct Modal
  openConductJustification: (player: 'A' | 'B', action: string, playerName: string) => void;
  closeConductJustification: () => void;

  // Conduct Rules Modal
  openConductRules: () => void;
  closeConductRules: () => void;

  // Pending Game Start
  setPendingGameStart: (isPending: boolean) => void;
}

export const useModalState = (initialMatchStartOpen = false): ModalStates & ModalActions => {
  // États des modals
  const [timerModal, setTimerModal] = useState<TimerModalState>({
    isOpen: false,
    timer: null
  });

  const [gameEndModal, setGameEndModal] = useState<GameEndModalState>({
    isOpen: false,
    winner: null
  });

  const [matchStartModal, setMatchStartModal] = useState<MatchStartModalState>({
    isOpen: initialMatchStartOpen
  });

  const [warningModal, setWarningModal] = useState<WarningModalState>({
    isOpen: false
  });

  const [warningStep, setWarningStepState] = useState<WarningStepState>({
    step: 1,
    selectedPlayer: null,
    selectedPlayerName: ''
  });

  const [conductJustificationModal, setConductJustificationModal] = useState<ConductJustificationModalState>({
    isOpen: false,
    player: null,
    action: null,
    playerName: ''
  });

  const [conductRulesModal, setConductRulesModal] = useState<ConductRulesModalState>({
    isOpen: false
  });

  const [pendingGameStart, setPendingGameStartState] = useState<PendingGameStartState>({
    isPending: false
  });

  // Actions pour les modals
  const modalActions: ModalActions = {
    // Timer Modal
    openTimerSelection: () => setTimerModal({ isOpen: true, timer: null }),
    openTimerWithTimer: (timer) => setTimerModal({ isOpen: true, timer }),
    closeTimer: () => setTimerModal({ isOpen: false, timer: null }),

    // Game End Modal
    openGameEnd: (winner) => setGameEndModal({ isOpen: true, winner }),
    closeGameEnd: () => setGameEndModal({ isOpen: false, winner: null }),

    // Match Start Modal
    openMatchStart: () => setMatchStartModal({ isOpen: true }),
    closeMatchStart: () => setMatchStartModal({ isOpen: false }),

    // Warning Modal
    openWarning: () => setWarningModal({ isOpen: true }),
    closeWarning: () => {
      setWarningModal({ isOpen: false });
      setWarningStepState({ step: 1, selectedPlayer: null, selectedPlayerName: '' });
    },
    setWarningStep: (step, player = null, playerName = '') => {
      setWarningStepState({ step, selectedPlayer: player, selectedPlayerName: playerName });
    },

    // Conduct Modal
    openConductJustification: (player, action, playerName) => {
      setConductJustificationModal({
        isOpen: true,
        player,
        action,
        playerName
      });
    },
    closeConductJustification: () => {
      setConductJustificationModal({
        isOpen: false,
        player: null,
        action: null,
        playerName: ''
      });
    },
  
    // Conduct Rules Modal
    openConductRules: () => setConductRulesModal({ isOpen: true }),
    closeConductRules: () => setConductRulesModal({ isOpen: false }),

    // Pending Game Start
    setPendingGameStart: (isPending: boolean) => setPendingGameStartState({ isPending })
  };

  return {
    // États
    timerModal,
    gameEndModal,
    matchStartModal,
    warningModal,
    warningStep,
    conductJustificationModal,
    conductRulesModal,
    pendingGameStart,
    // Actions
    ...modalActions
  };
};